// components/AppFrame.tsx — V29 S1 shell.
//
// The frame between AuthGate and the routed screens. V29 replaces the hidden
// hamburger-drawer navigation with an always-visible bottom TabBar on the five
// section roots, and the transparent SwipeHint with the opaque ChatDock pill.
// EdgeNavZones (the invisible 30px tap strips) are gone — navigation is
// explicit: TabBar, per-screen buttons, and the chrome back arrow.
//
// Responsibilities:
//   - mount ChatSheetProvider (one global sheet state for every student route)
//   - expose useShellNav() — the shared back/next resolution (history-aware,
//     screen-overridable) that TopChrome's back arrow and per-screen docks use
//   - render {children}, then <TabBar> + <ChatDock> + <GlobalChatSheet> overlays
//
// AuthGate boundary (F24): AppFrame is rendered INSIDE AuthGate (App.tsx), so
// no authenticated chrome — tab bar included — exists before /api/me validates.
//
// Simula durability (F25): TabBar taps are pure navigation (W3 semantics); no
// shell affordance resets session state or a pending finalize.

import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChatSheetProvider } from '../state/chat-sheet';
import { NavOverrideProvider } from '../state/nav-override';
import { useScreenFlow } from '../state/screen-flow';
import { useBrowserNavigationGuard } from '../hooks/useBrowserNavigationGuard';
import { TABBAR_H } from '../tokens';
import { TabBar } from './TabBar';
import { ChatDock } from './ChatDock';
import { GlobalChatSheet } from './GlobalChatSheet';

/**
 * Resolve a FLOW route template (which may contain `:param` tokens) against the
 * current pathname. Returns the concrete path, or `null` when a placeholder
 * can't be filled from the current URL (caller then falls back to history).
 */
function resolveRoute(template: string, pathname: string): string | null {
  if (!template.includes(':')) return template;

  const segs = pathname.split('/').filter(Boolean);

  const lookups: Record<string, () => string | undefined> = {
    argomentoId: () => {
      // /studia/argomento/:id  · /studia/lezione/deep/:id
      const sa = segs.indexOf('argomento');
      if (sa >= 0 && segs[sa + 1]) return segs[sa + 1];
      const sd = segs.indexOf('deep');
      if (sd >= 0 && segs[sd + 1]) return segs[sd + 1];
      // /studia/lezione/guidata/:argomentoId (FP5 guided lesson)
      const sg = segs.indexOf('guidata');
      if (sg >= 0 && segs[sg + 1]) return segs[sg + 1];
      // /appendice/:argomentoId/:principleId
      const ap = segs.indexOf('appendice');
      if (ap >= 0 && segs[ap + 1]) return segs[ap + 1];
      return undefined;
    },
    principleId: () => {
      const ap = segs.indexOf('appendice');
      if (ap >= 0 && segs[ap + 2]) return segs[ap + 2];
      return undefined;
    },
  };

  let unresolved = false;
  const out = template.replace(/:([A-Za-z0-9_]+)/g, (_m, name: string) => {
    const value = lookups[name]?.();
    if (value === undefined) {
      unresolved = true;
      return `:${name}`;
    }
    return value;
  });

  return unresolved ? null : out;
}

interface ShellNavValue {
  /** Shared "back" resolution: screen-registered onBack → history → FLOW back. */
  goBack: () => void;
  /** Shared "next" resolution: screen-registered onNext → FLOW next. */
  goNext: () => void;
}

interface ShellNavigationLockValue {
  locked: boolean;
  setLocked: (locked: boolean) => void;
}

const ShellNavigationLockContext = createContext<ShellNavigationLockValue | null>(null);

/**
 * Publish a screen-owned navigation lock to the app shell. The shell stores no
 * recovery material: only whether navigation must stay contained while a
 * one-time secret is pending or visible.
 */
export function useShellNavigationLock(locked: boolean): void {
  const setLocked = useContext(ShellNavigationLockContext)?.setLocked;

  useEffect(() => {
    if (!setLocked) return;
    setLocked(locked);
    return () => { setLocked(false); };
  }, [locked, setLocked]);
}

/**
 * Shell navigation actions. Stateless — computed from the router + flow
 * metadata, so it works anywhere under a Router (screens rendered in isolation
 * in unit tests included; nav-override degrades to a no-op registry there,
 * matching the useNavOverride convention).
 */
export function useShellNav(): ShellNavValue {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const flow = useScreenFlow();
  const navigationLocked = useContext(ShellNavigationLockContext)?.locked ?? false;

  const goBack = useCallback(() => {
    if (navigationLocked) return;
    // 1. A screen-registered imperative onBack wins (Simula prev-question,
    //    guided-lesson prev chapter). It owns the meaning of "back" there.
    if (flow.navActions?.onBack) {
      flow.navActions.onBack();
      return;
    }
    // 2. History-aware back: if there's in-app back history, step through it so
    //    "back" returns to the PREVIOUS page, not the section home. React Router
    //    v7 stores its history position in `history.state.idx`.
    const historyIdx =
      typeof window !== 'undefined'
        ? ((window.history.state as { idx?: number } | null)?.idx ?? 0)
        : 0;
    if (historyIdx > 0) {
      navigate(-1);
      return;
    }
    // 3. No history (deep-link / first page) → resolve the static FLOW back
    //    route. Unresolvable param → best-effort history back.
    if (!flow.back) return;
    const target = resolveRoute(flow.back, pathname);
    if (target) navigate(target);
    else navigate(-1);
  }, [navigationLocked, flow.navActions, flow.back, pathname, navigate]);

  const goNext = useCallback(() => {
    if (navigationLocked) return;
    // 1. A screen-registered imperative onNext wins.
    if (flow.navActions?.onNext) {
      flow.navActions.onNext();
      return;
    }
    // 2. Otherwise resolve the static FLOW next route (incl. a screen-registered
    //    nextOverride route merged into flow.next).
    if (!flow.next) return;
    const target = resolveRoute(flow.next, pathname);
    if (target) navigate(target);
  }, [navigationLocked, flow.navActions, flow.next, pathname, navigate]);

  return useMemo<ShellNavValue>(() => ({ goBack, goNext }), [goBack, goNext]);
}

// Inner frame — must live UNDER ChatSheetProvider so overlays read useChatSheet.
function AppFrameInner({ children }: { children: ReactNode }) {
  const flow = useScreenFlow();
  const navigationLocked = useContext(ShellNavigationLockContext)?.locked ?? false;

  return (
    <div
      // Chat-enabled screens reserve bottom space in their scroll regions for
      // the ChatDock ([data-has-chat] rule in global.css); tab-root screens
      // reserve TabBar clearance the same way ([data-has-tabbar]).
      data-has-chat={flow.chat ? 'true' : undefined}
      style={{
        position:      'relative',
        // height:100% (not flex:1): #root is a height:100% block, not a flex
        // column, so the Screen height:100% chain only resolves if this wrapper
        // is itself full-height (else short screens collapse — the white band).
        height:        '100%',
        minHeight:     0,
        display:       'flex',
        flexDirection: 'column',
        // Full-bleed app shell: clip everything to the viewport so off-canvas
        // overlays (the closed GlobalChatSheet at translateY(100%)) do NOT
        // extend the document's scrollable area. Screens own their internal
        // scroll (asc-scroll regions); the body never scrolls.
        overflow:      'hidden',
      }}
    >
      <TabBarClearance>{children}</TabBarClearance>

      {/* Opaque "Chiedi al Prof" dock (gated on flow.chat + sheet closed). */}
      <ChatDock />

      {/* Bottom tab bar — permanent on every student screen (DP3). */}
      <TabBar navigationLocked={navigationLocked} />

      {/*
        Global "Chiedi a Il Prof" sheet. Mounted ONCE here so every
        chat-enabled student route shares one sheet instance; it reads
        useChatSheet() for open/close (<ChatDock> is the opener) and renders
        <ProfChatPanel/> over the scoped thread.
      */}
      <GlobalChatSheet />
    </div>
  );
}

function ShellNavigationLockProvider({ children }: { children: ReactNode }) {
  const [locked, setLocked] = useState(false);
  useBrowserNavigationGuard(locked);

  const value = useMemo<ShellNavigationLockValue>(
    () => ({ locked, setLocked }),
    [locked],
  );

  return (
    <ShellNavigationLockContext.Provider value={value}>
      {children}
    </ShellNavigationLockContext.Provider>
  );
}

// Reserves TabBar clearance exactly when the bar is visible (section root AND
// not hidden by a screen — same source of truth as TabBar). Padding on the
// CONTAINER (not the scroll regions) so pinned bottom elements — the Il Prof
// composer — are lifted above the fixed bar too, not just scrolled content.
function TabBarClearance({ children }: { children: ReactNode }) {
  // DP3 (2026-07-13): the TabBar is permanent, so the clearance is too.
  // Everything inside (screens, their pinned docks and absolute pills) sits
  // above the bar automatically because percentage heights resolve against
  // this padded content box.
  return (
    <div
      data-has-tabbar="true"
      style={{
        display:       'flex',
        flexDirection: 'column',
        height:        '100%',
        minHeight:     0,
        paddingBottom: `calc(${TABBAR_H}px + var(--sai-bottom))`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Public wrapper. Mounts the chat-sheet provider so children + the frame share
 * one sheet instance, then renders the shell frame.
 */
export function AppFrame({ children }: { children: ReactNode }) {
  return (
    <ChatSheetProvider>
      <ShellNavigationLockProvider>
        {/* NavOverrideProvider must wrap AppFrameInner (which reads useScreenFlow
            for goBack/goNext) AND the children (screens that register dynamic
            nav, e.g. Appendice detail's next sibling). */}
        <NavOverrideProvider>
          <AppFrameInner>{children}</AppFrameInner>
        </NavOverrideProvider>
      </ShellNavigationLockProvider>
    </ChatSheetProvider>
  );
}
